// Public AI feature flags exposed by the BFF. The front uses these to hide/show
// AI-driven UI (smart replies chips, photo coach button, "para você" feed, etc).
// Refreshed on app boot + every 5 minutes; safe-by-default to all-off when BFF
// is unreachable so we never accidentally show an experimental UI to users.

import { getAuthConfig } from "@/config/auth";

export type AIRolloutMode = "disabled" | "shadow" | "warn" | "enforce";

export interface AIFeatures {
  listing_draft_v2: boolean;
  photo_coach: boolean;
  product_match: boolean;
  smart_replies: boolean;
  semantic_search: boolean;
  nl_search: boolean;
  image_search: boolean;
  personalized_feed: boolean;
  similar_items: boolean;
  fair_price: boolean;
  counter_offer_suggest: boolean;
  chat_summary: boolean;
  moderation_mode: AIRolloutMode;
  safety_guardrail_mode: AIRolloutMode;
}

export const DEFAULT_AI_FEATURES: AIFeatures = {
  listing_draft_v2: false,
  photo_coach: false,
  product_match: false,
  smart_replies: false,
  semantic_search: false,
  nl_search: false,
  image_search: false,
  personalized_feed: false,
  similar_items: false,
  fair_price: false,
  counter_offer_suggest: false,
  chat_summary: false,
  moderation_mode: "disabled",
  safety_guardrail_mode: "disabled",
};

let cache: { features: AIFeatures; fetchedAt: number } | null = null;
const TTL_MS = 5 * 60 * 1000;

/** Returns AI features from cache or fetches from BFF. Defaults to all-off on failure. */
export async function getAIFeatures(): Promise<AIFeatures> {
  const now = Date.now();
  if (cache && now - cache.fetchedAt < TTL_MS) return cache.features;
  const { baseUrl } = getAuthConfig();
  if (!baseUrl) return DEFAULT_AI_FEATURES;
  try {
    const res = await fetch(`${baseUrl.replace(/\/$/, "")}/ai/features`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return DEFAULT_AI_FEATURES;
    const data = (await res.json()) as Partial<AIFeatures>;
    const merged: AIFeatures = { ...DEFAULT_AI_FEATURES, ...data };
    cache = { features: merged, fetchedAt: now };
    return merged;
  } catch {
    return DEFAULT_AI_FEATURES;
  }
}

/** Force-refresh; useful for ops/QA to verify flag changes without page reload. */
export async function refreshAIFeatures(): Promise<AIFeatures> {
  cache = null;
  return getAIFeatures();
}
